/**
 * Rechnungsdetail (sevdesk-Logik): Positionen, Steuer je Satz,
 * Zahlungshistorie, Storno-Verweis, Aktionen.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Download, Mail, Ban, FilePlus2, CheckCircle2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { getInvoiceDetail } from '@/lib/invoiceApi';

const KIND = {
  payment: 'Zahlung',
  skonto: 'Skonto',
  credit_note: 'Gutschrift',
  storno: 'Storno',
  chargeback: 'Rücklastschrift',
  credit_used: 'Guthaben verrechnet',
  migration_adjustment: 'Minderung (Altbestand)',
};
const date = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '—');

export default function InvoiceDetailSheet({ invoiceId, open, onOpenChange, actions = {} }) {
  const { data: inv, isLoading, error } = useQuery({
    queryKey: ['fibu-invoice', invoiceId],
    queryFn: () => getInvoiceDetail(invoiceId),
    enabled: !!invoiceId && open,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        {!inv && (
          <SheetHeader>
            <SheetTitle>Rechnung</SheetTitle>
            <SheetDescription>{error ? error.message : 'Lädt …'}</SheetDescription>
          </SheetHeader>
        )}
        {isLoading && <p className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /></p>}
        {inv && (
          <>
            <SheetHeader>
              <SheetTitle className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono">{inv.invoice_no || 'Entwurf'}</span>
                <span className="text-sm font-normal text-slate-500">{inv.customer_name}</span>
              </SheetTitle>
              <SheetDescription>
                Rechnungsdatum {date(inv.invoice_date)} · fällig {date(inv.due_date)}
                {inv.delivery_date && ` · Leistung ${date(inv.delivery_date)}`}
                {inv.source === 'migration' && ' · übernommen aus Altbestand'}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-5 space-y-5">
              <p className="font-display text-[32px] leading-none font-extrabold tracking-[-0.02em] tabular-nums">{fmtEUR(inv.gross_total)}</p>

              {inv.storno && (
                <p className="text-sm bg-slate-100 border border-slate-200 rounded-md p-3">
                  Storniert durch <span className="font-mono">{inv.storno.invoice_no}</span> am {date(inv.storno.invoice_date)}
                  {inv.cancel_reason && ` — ${inv.cancel_reason}`}
                </p>
              )}

              <section>
                <h3 className="text-sm font-semibold mb-2">Positionen</h3>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 text-left">
                    <tr><th className="py-1">Bezeichnung</th><th className="py-1 text-right">Menge</th><th className="py-1 text-right">Preis</th><th className="py-1 text-right">USt</th><th className="py-1 text-right">Netto</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {inv.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="py-1.5 pr-2">{l.description}{Number(l.discount_pct) > 0 && <span className="text-xs text-orange-700"> −{Number(l.discount_pct)} %</span>}</td>
                        <td className="py-1.5 text-right tabular-nums">{Number(l.quantity).toLocaleString('de-DE')} {l.unit}</td>
                        <td className="py-1.5 text-right tabular-nums">{Number(l.unit_price_net).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                        <td className="py-1.5 text-right tabular-nums">{Number(l.tax_rate)} %</td>
                        <td className="py-1.5 text-right tabular-nums">{fmtEUR(l.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <dl className="mt-3 ml-auto max-w-xs text-sm space-y-1">
                  {Number(inv.kundenrabatt_prozent) > 0 && <div className="flex justify-between text-orange-700"><dt>Kundenrabatt</dt><dd>{Number(inv.kundenrabatt_prozent)} %</dd></div>}
                  <div className="flex justify-between"><dt className="text-slate-500">Netto</dt><dd className="tabular-nums">{fmtEUR(inv.net_total)}</dd></div>
                  {(inv.tax_breakdown || []).map((g) => (
                    <div key={g.tax_rate} className="flex justify-between"><dt className="text-slate-500">USt {Number(g.tax_rate)} %</dt><dd className="tabular-nums">{fmtEUR(g.tax)}</dd></div>
                  ))}
                  <div className="flex justify-between font-semibold border-t border-slate-200 pt-1"><dt>Gesamt</dt><dd className="tabular-nums">{fmtEUR(inv.gross_total)}</dd></div>
                  {inv.skonto_amount && <div className="flex justify-between text-emerald-700 text-xs"><dt>Skonto bis {date(inv.skonto_until)}</dt><dd className="tabular-nums">−{fmtEUR(inv.skonto_amount)}</dd></div>}
                </dl>
              </section>

              <section>
                <h3 className="text-sm font-semibold mb-2">Zahlungshistorie</h3>
                {inv.payments.length === 0 ? (
                  <p className="text-sm text-slate-500">Noch keine Zahlungen.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 text-sm">
                    {inv.payments.map((p) => (
                      <li key={p.id} className={`flex justify-between gap-2 py-1.5 ${p.reversed_at ? 'line-through text-slate-400' : ''}`}>
                        <span>{date(p.payment_date)} · {KIND[p.kind] || p.kind}{p.note ? ` · ${p.note}` : ''}</span>
                        <span className="tabular-nums">{fmtEUR(p.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                {actions.pdf && <Button variant="outline" onClick={() => actions.pdf(inv)}><Download className="w-4 h-4 mr-2" />PDF</Button>}
                {actions.mail && inv.order_id && inv.status !== 'cancelled' && <Button variant="outline" onClick={() => actions.mail(inv)}><Mail className="w-4 h-4 mr-2" />Per E-Mail{inv.sent_at && <CheckCircle2 className="w-3.5 h-3.5 ml-1.5 text-emerald-600" />}</Button>}
                {actions.gutschrift && ['open', 'partial', 'paid'].includes(inv.status) && <Button variant="outline" onClick={() => actions.gutschrift(inv)}><FilePlus2 className="w-4 h-4 mr-2" />Gutschrift</Button>}
                {actions.storno && ['open', 'partial', 'paid'].includes(inv.status) && <Button variant="outline" className="text-red-700" onClick={() => actions.storno(inv)}><Ban className="w-4 h-4 mr-2" />Stornieren</Button>}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
